// -- External libraries
import {
    createEffect,
    createSignal,
    lazy,
    Show,
} from 'solid-js'
import { PublicTemplateContext } from './public-template-component-context.js' // Adjusted import

import Routes from '../../../routes/router'
import { usePublicTemplateComponentController } from './public-template-component-controller.js'
import './public-template-component.scss'
import Logo from '/assets/logo/logo.svg'
import { useLocation } from '@solidjs/router'
import BarAtomComponent from '../../atoms/bar-atom/bar-atom-component.jsx'
import SiteHeader from '../../organisms/site-header/site-header.jsx'
import ButtonAtomComponent from '../../atoms/button-atom/button-atom-component.jsx'
import ImageAtomComponent from '../../atoms/image-atom/image-atom-component.jsx'
import LinkAtomComponent from '../../atoms/link-atom/link-atom-component.jsx'
import SeparatorAtomComponent from '../../atoms/separator-atom/separator-atom-component.jsx'
import MenuMoleculeComponent from '../../molecules/menu-molecule/menu-molecule-component.jsx'
import FooterOrganismComponent from '../../organisms/footer-organism/footer-organism.jsx'
import BannerMoleculeComponent from '../../molecules/banner-molecule-component/banner-molecule-component.jsx'
import LikertMoleculeComponent from '../../molecules/likert-molecule/likert-molecule-component.jsx'
import CustomSVG from '../../atoms/custom-svg.jsx'
import { usePartner } from '../../../contexts/partner-context.jsx'

// All four are only ever mounted behind a `<Show>` below (never needed for
// first paint on any route), so their component code is code-split out of
// the boot chunk instead of being eagerly bundled into every page — same
// `lazy()` pattern already used elsewhere (e.g. `payment-methods-modal-row.jsx`).
const ReferralSenderFeedbackModal = lazy(() => import('../../../modals/referral-sender-feedback-modal/referral-sender-feedback-modal.jsx'))
const LocaleSelectModal = lazy(() => import('../../../modals/locale-select-modal/locale-select-modal.jsx'))
const ReferralRecipientFeedbackModal = lazy(() => import('../../../modals/referral-recipient-feedback-modal/referral-recipient-feedback-modal.jsx'))
// Only reachable on the two property-setup wizard routes, so keeping it lazy
// also keeps the wizard's step config (and the help content) off every other page.
const LodgrWizardHelp = lazy(() => import('../../lodgr/lodgr-wizard-help/lodgr-wizard-help.jsx'))

import { ROUTES } from '../../../routes/router-utils.js'
import { getLocaleHref } from '../../../utils/locale-navigation.js'
import { getPathWithoutLocalePrefix } from '../../../utils/locale-path.js'
import environment, { platform } from '../../../../../.configs/app/environment.js'
import { useIntl } from '@cookbook/solid-intl'
import { messages } from './public-template-component.messages.en.js'

// Marketing pages render their hero from static content and don't need currency
// or authenticated counts to paint. They bypass the data gate below so they show
// immediately; data-dependent routes (search, dashboards) still wait for `isLoaded`.
// Paths are compared after stripping any locale prefix (e.g. /fr-ca/become-a-host).
//
// The 5 secondary marketing pages below were audited and added here too: each
// fetches (or needs) none of countStore's auth/currency-derived data for its own
// body content —
//   /newsroom fetches its own articles via NewsroomService in onMount and tracks
//     admin role from sessionStore independently of countStore;
//   /savings' calculator (LodgrSavingsCalculator) loads its own PlatformService
//     config and is pre-fill-free (manual defaults + optional URL params only);
//   /demo and /request-listing-setup are static contact forms (ContactService submit on demand);
//   /points and /faq are fully static copy/FAQ accordions with no data fetch.
const IMMEDIATE_RENDER_PATHS = new Set([
    '/',
    '/become-a-host',
    '/browser-extension',
    '/newsroom',
    '/savings',
    '/demo',
    '/request-listing-setup',
    '/points',
    '/faq',
    // TENANT-5421: static conference marketing page — placeholder card data
    // lives in its controller, no data fetch to gate on.
    '/lodgr-insights',
])

// `/faq` self-redirects to `/faq/<category>` (see `faq-page-controller.js`'s
// mount effect) via a `replace` navigation that fires client-side almost
// immediately after mount — before this component's next reactive read of
// `location.pathname`. Without this prefix, that redirect would land outside
// `IMMEDIATE_RENDER_PATHS` (exact-match only) and could yank the just-painted
// FAQ content back behind the data gate if `countStore.isLoaded` hadn't
// resolved yet. All `/faq/*` categories render the same static component, so
// treating the whole subtree as immediate-render is safe.
const IMMEDIATE_RENDER_PREFIXES = ['/faq/']

const NavigationSectionHeading = (props) => (
    <div class="public-template-component__nav-section-heading">
        <CustomSVG class="public-template-component__nav-section-heading__icon" path={props.iconPath} />
        {props.children}
    </div>
)

export default function PublicTemplateComponent (props) {
    const controller = usePublicTemplateComponentController(props)
    const location = useLocation()
    const intl = useIntl()
    const {
        partnerConfig,
    } = usePartner()

    const [
        selected,
        setSelected,
    ] = createSignal(false)

    const isImmediateRenderRoute = () => {
        const path = getPathWithoutLocalePrefix(location.pathname)
        const normalised = path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path
        if (IMMEDIATE_RENDER_PATHS.has(normalised)) return true
        return IMMEDIATE_RENDER_PREFIXES.some((prefix) => normalised.startsWith(prefix))
    }

    const showPartnerHeader = () => controller.state.showPartnerHeader && !!partnerConfig()
    const partnerSearchHref = () => {
        const partnerSlug = partnerConfig()?.slug
        return partnerSlug ? getLocaleHref(`/partners/${partnerSlug}/search`) : getLocaleHref('/')
    }

    createEffect(() => {
        if (location.pathname.startsWith('/property/') || location.pathname === '/properties') {
            setSelected(true)
        } else {
            setSelected(false)
        }
    })

    return (
        <PublicTemplateContext.Provider value={controller.value}>
            <div class={['public-template-component', controller.styles.class].join(' ')}>
                <Show when={controller.state.showHeader}>
                    <div
                        class="public-template-component__header-wrapper"
                        classList={{
                            'public-template-component__header-wrapper--dashboard': controller.state.showSidebar,
                        }}
                    >
                        <BarAtomComponent
                            class="public-template-component__header"
                            classList={{
                                'public-template-component__header--dashboard': controller.state.showSidebar || controller.state.showFullWidthHeader,
                                'public-template-component__header--partner': showPartnerHeader(),
                            }}
                        >
                            <Show
                                when={showPartnerHeader()}
                                fallback={
                                    <SiteHeader
                                        isAuthenticated={controller.isAuthenticated()}
                                        locale={intl.locale}
                                        logoSrc={Logo}
                                        onExtensionClick={controller.handleExtensionClick}
                                        onHostClick={controller.handleHostClick}
                                        onNewsroomClick={controller.handleNewsroomClick}
                                        onSearchClick={controller.handleSearchClick}
                                        onSignInClick={controller.isAuthenticated()
                                            ? controller.navigateToDashboard
                                            : controller.openAuthModal}
                                        showMenu={controller.state.showHeaderMenu}
                                    />
                                }
                            >
                                <LinkAtomComponent
                                    class="public-template-component__header__partner-link"
                                    href={partnerSearchHref()}
                                    onClick={controller.handleLogoClick}
                                >
                                    <ImageAtomComponent
                                        class="public-template-component__header__partner-logo"
                                        src={partnerConfig()?.logoUrl}
                                        alt={partnerConfig()?.displayName}
                                    />
                                </LinkAtomComponent>
                                <LinkAtomComponent
                                    class="public-template-component__header__partner-link--mobile"
                                    href={partnerSearchHref()}
                                    onClick={controller.handleLogoClick}
                                >
                                    <ImageAtomComponent
                                        class="public-template-component__header__partner-logo public-template-component__header__partner-logo--mobile"
                                        src={partnerConfig()?.logoUrl}
                                        alt={partnerConfig()?.displayName}
                                    />
                                </LinkAtomComponent>
                            </Show>
                            <Show when={controller.state.showMobileMenu}>
                                <ButtonAtomComponent
                                    onClick={controller.toggleHamburgerMenu}
                                    class="public-template-component__header__hamburger"
                                >
                                    <ImageAtomComponent
                                        src={
                                            controller.showHamburgerMenu()
                                                ? '/assets/icons/close-filled.svg'
                                                : '/assets/icons/hamburger-outlined.svg'
                                        }
                                    />
                                </ButtonAtomComponent>
                            </Show>
                            {/*
                                TENANT-5433: the locale/currency control lives
                                ONLY inside the hamburger overlay on mobile, so
                                a route that suppresses the hamburger loses it
                                entirely below 768px. This standalone control
                                takes its place — same action, same icon —
                                and renders only when the mobile menu is off,
                                so routes that keep their hamburger are
                                untouched.
                            */}
                            <Show when={!controller.state.showMobileMenu}>
                                <ButtonAtomComponent
                                    aria-label={intl.formatMessage(messages.languageAndCurrencyButton)}
                                    class="public-template-component__header__locale"
                                    onClick={controller.handleLocaleSelect}
                                >
                                    <CustomSVG path="icons/earth-americas-outlined" />
                                </ButtonAtomComponent>
                            </Show>
                            <div class="public-template-component__checkout-header">
                                <Show when={controller.state.wizardHelpSection}>
                                    <LodgrWizardHelp sectionKey={controller.state.wizardHelpSection} />
                                </Show>

                                <ButtonAtomComponent
                                    class="public-template-component__checkout-header__locale-selector"
                                    onClick={controller.handleLocaleSelect}
                                >
                                    <CustomSVG class="public-template-component__checkout-header__locale-selector__icon" path="icons/earth-americas-outlined"/>
                                </ButtonAtomComponent>

                                {/* TODO: User header should show on all "dashboard" pages. We should update our approach so we don't have to state "showUserHeader" for each page. */}
                                <Show when={controller.state.showUserHeader}>
                                    <Show when={controller.isAuthenticated()}
                                        fallback={
                                            <Show when={!showPartnerHeader()}>
                                                <ButtonAtomComponent
                                                    class="public-template-component__checkout-header__button public-template-component__checkout-header__button--primary"
                                                    onClick={controller.openAuthModal}
                                                >
                                                    {intl.formatMessage(messages.loginButton)}
                                                </ButtonAtomComponent>
                                            </Show>
                                        }
                                    >
                                        {/* TODO: use TooltipWrapperMoleculeComponent */}
                                        <div
                                            class="public-template-component__checkout-header__user-image-wrapper"
                                            ref={controller.setProfileReference}
                                            onMouseEnter={controller.showProfileTooltip}
                                            onMouseLeave={controller.hideProfileTooltip}
                                        >
                                            <img
                                                src={'/assets/icons/user.svg'}
                                                class="public-template-component__checkout-header__user-image"
                                            />

                                            <div
                                                class="public-template-component__checkout-header__user-image-tooltip"
                                                classList={{
                                                    'public-template-component__checkout-header__user-image-tooltip--hidden':
                                                        !controller.profileTooltipVisible(),
                                                    'public-template-component__checkout-header__user-image-tooltip--open-left':
                                                        controller.showTooltipLeft(),
                                                }}
                                                ref={controller.setProfileTooltipReference}
                                            >
                                                <div class="public-template-component__checkout-header__user-image-tooltip__content">
                                                    {controller.bookingStore.getEmail()}
                                                </div>
                                            </div>
                                        </div>
                                    </Show>
                                </Show>
                                <Show when={showPartnerHeader()}>
                                    <LinkAtomComponent
                                        class="public-template-component__checkout-header__lodgr-link"
                                        href={window.location.origin}
                                        external
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={intl.formatMessage(messages.openLodgrInNewTabAriaLabel)}
                                    >
                                        <ImageAtomComponent
                                            class="public-template-component__checkout-header__lodgr-link__icon"
                                            src="/assets/logo/lodgr-square-logo.svg"
                                            alt="Lodgr"
                                        />
                                    </LinkAtomComponent>
                                </Show>
                            </div>
                        </BarAtomComponent>
                    </div>
                </Show>

                <Show when={controller.showHamburgerMenu()}>
                    <BarAtomComponent class="public-template-component__overlay" direction="vertical">
                        <Show when={!controller.isAuthenticated()}>
                            <MenuMoleculeComponent
                                class="public-template-component__overlay__menu"
                                direction="vertical"
                            >
                                <NavigationSectionHeading iconPath="icons/suitcase-outlined">
                                    {intl.formatMessage(messages.guestSectionLabel)}
                                </NavigationSectionHeading>
                                <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/search" onClick={controller.handleSearchClick}>
                                    {intl.formatMessage(messages.searchLink)}
                                </LinkAtomComponent>
                                {/* TENANT-5565: the mobile app's way into Lodgr Insights; the website never routes /insights. */}
                                <Show when={platform.isMobileApp}>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/insights" onClick={controller.handleInsightsClick}>
                                        {intl.formatMessage(messages.lodgrInsightsButton)}
                                    </LinkAtomComponent>
                                </Show>
                                <SeparatorAtomComponent class="public-template-component__overlay__separator" />
                                <ButtonAtomComponent
                                    class="public-template-component__overlay__menu__button"
                                    onClick={controller.handleLocaleSelect}
                                >
                                    <CustomSVG path="icons/earth-americas-outlined" class="public-template-component__overlay__menu__button__icon"/>
                                    {intl.formatMessage(messages.languageAndCurrencyButton)}
                                </ButtonAtomComponent>
                                <SeparatorAtomComponent class="public-template-component__overlay__separator" />
                                {/* Not in the mobile app: these surfaces are web-only (the extension cannot exist on a phone), so the app does not carry them. */}
                                <Show when={!platform.isMobileApp}>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/newsroom" onClick={controller.handleNewsroomClick}>
                                        {intl.formatMessage(messages.newsroomButton)}
                                    </LinkAtomComponent>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/browser-extension" onClick={controller.handleExtensionClick}>
                                        {intl.formatMessage(messages.browserExtensionButton)}
                                    </LinkAtomComponent>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/become-a-host" onClick={controller.handleHostClick}>
                                        {intl.formatMessage(messages.becomeAHostButton)}
                                    </LinkAtomComponent>
                                </Show>
                                <LinkAtomComponent href="/faq/general" onClick={controller.handleFaqClick}>{intl.formatMessage(messages.faqLink)}</LinkAtomComponent>
                                {/* Mobile app only: the footer normally carries the legal links, and the app does not render it (R9/R10). */}
                                <Show when={platform.isMobileApp}>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/tos" onClick={controller.handleLegalLinkClick}>
                                        {intl.formatMessage(messages.termsLink)}
                                    </LinkAtomComponent>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/privacy-policy" onClick={controller.handleLegalLinkClick}>
                                        {intl.formatMessage(messages.privacyLink)}
                                    </LinkAtomComponent>
                                </Show>
                                <Show when={!showPartnerHeader()}>
                                    <ButtonAtomComponent
                                        class="public-template-component__overlay__menu__button"
                                        onClick={controller.openAuthModal}
                                    >
                                        {intl.formatMessage(messages.loginButton)}
                                    </ButtonAtomComponent>
                                </Show>
                            </MenuMoleculeComponent>
                        </Show>

                        <MenuMoleculeComponent class="public-template-component__overlay__menu" direction="vertical">
                            <Show when={controller.isAuthenticated()}>
                                <NavigationSectionHeading iconPath="icons/suitcase-outlined">
                                    {intl.formatMessage(messages.guestSectionLabel)}
                                </NavigationSectionHeading>
                                <Show when={environment.features.search}>
                                    <LinkAtomComponent
                                        class="public-template-component__main__sidebar__menu__item"
                                        href="/search"
                                        onClick={controller.handleSearchClick}
                                    >
                                        {intl.formatMessage(messages.searchLink)}
                                    </LinkAtomComponent>
                                    {/* TENANT-5565: the mobile app's way into Lodgr Insights; the website never routes /insights. */}
                                    <Show when={platform.isMobileApp}>
                                        <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/insights" onClick={controller.handleInsightsClick}>
                                            {intl.formatMessage(messages.lodgrInsightsButton)}
                                        </LinkAtomComponent>
                                    </Show>
                                </Show>
                                <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/stays" onClick={controller.handleStaysClick}>
                                    {intl.formatMessage(messages.staysLink)}
                                </LinkAtomComponent>
                                <SeparatorAtomComponent class="public-template-component__overlay__separator" />
                                <NavigationSectionHeading iconPath="icons/house-outlined">
                                    {intl.formatMessage(messages.hostSectionLabel)}
                                </NavigationSectionHeading>
                                <LinkAtomComponent
                                    class="public-template-component__main__sidebar__menu__item"
                                    href="/properties"
                                    onClick={controller.handlePropertiesClick}
                                >
                                    {intl.formatMessage(messages.propertiesLink)}
                                </LinkAtomComponent>
                                <Show when={controller.countStore.get('numOfProperties') > 0}>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/bookings" onClick={controller.handleBookingsClick}>
                                        {intl.formatMessage(messages.bookingsLink)}
                                    </LinkAtomComponent>
                                    {/* TENANT-5636: the host calendar (TENANT-5285) reached the desktop sidebar
                                        but not this overlay. Same ownership gate as Bookings above, same click
                                        handler as the sidebar link (analytics + closes the overlay). */}
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/calendar" onClick={controller.handleCalendarClick}>
                                        {intl.formatMessage(messages.calendarLink)}
                                    </LinkAtomComponent>
                                </Show>
                                <SeparatorAtomComponent class="public-template-component__overlay__separator" />
                                <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/inbox" onClick={controller.handleInboxClick}>{intl.formatMessage(messages.inboxLink)}</LinkAtomComponent>
                                <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/account" onClick={controller.handleAccountClick}>
                                    {intl.formatMessage(messages.accountLink)}
                                </LinkAtomComponent>

                                <SeparatorAtomComponent class="public-template-component__overlay__separator" />
                                <ButtonAtomComponent
                                    class="public-template-component__overlay__menu__button"
                                    onClick={controller.handleLocaleSelect}
                                >
                                    <CustomSVG path="icons/earth-americas-outlined" class="public-template-component__overlay__menu__button__icon"/>
                                    {' '}
                                    {intl.formatMessage(messages.languageAndCurrencyButton)}
                                </ButtonAtomComponent>
                                <SeparatorAtomComponent class="public-template-component__overlay__separator" />
                                {/* Not in the mobile app: these surfaces are web-only (the extension cannot exist on a phone), so the app does not carry them. */}
                                <Show when={!platform.isMobileApp}>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/newsroom" onClick={controller.handleNewsroomClick}>
                                        {intl.formatMessage(messages.newsroomButton)}
                                    </LinkAtomComponent>
                                    <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/browser-extension" onClick={controller.handleExtensionClick}>
                                        {intl.formatMessage(messages.browserExtensionButton)}
                                    </LinkAtomComponent>
                                </Show>
                                <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href={ROUTES.REFERRAL_CENTER} onClick={controller.handleReferralsClick}>
                                    {intl.formatMessage(messages.referralsLink)}
                                </LinkAtomComponent>
                                <LinkAtomComponent
                                    class="public-template-component__overlay__dashboard-link"
                                    href="/faq/general"
                                    onClick={controller.handleFaqClick}
                                >
                                    {intl.formatMessage(messages.faqLink)}
                                </LinkAtomComponent>
                                {/* Mobile app only: the footer normally carries the legal links, and the app does not render it (R9/R10). */}
                                <Show when={platform.isMobileApp}>
                                    <LinkAtomComponent
                                        class="public-template-component__overlay__dashboard-link"
                                        href="/tos"
                                        onClick={controller.handleLegalLinkClick}
                                    >
                                        {intl.formatMessage(messages.termsLink)}
                                    </LinkAtomComponent>
                                    <LinkAtomComponent
                                        class="public-template-component__overlay__dashboard-link"
                                        href="/privacy-policy"
                                        onClick={controller.handleLegalLinkClick}
                                    >
                                        {intl.formatMessage(messages.privacyLink)}
                                    </LinkAtomComponent>
                                </Show>
                                <ButtonAtomComponent
                                    class="public-template-component__overlay__menu__button"
                                    onClick={controller.logout}
                                >
                                    {intl.formatMessage(messages.logOutLink)}
                                </ButtonAtomComponent>
                            </Show>
                        </MenuMoleculeComponent>
                    </BarAtomComponent>
                </Show>
                <Show when={!controller.showHamburgerMenu()}>
                    <div
                        class="public-template-component__main"
                        classList={{
                            'public-template-component__main--overflow': controller.state.showSidebar,
                        }}
                    >
                        {/* dashboard side menu */}
                        <Show when={controller.state.showSidebar}>
                            <BarAtomComponent class="public-template-component__main__sidebar" direction="vertical">
                                <MenuMoleculeComponent
                                    class="public-template-component__main__sidebar__menu"
                                    direction="vertical"
                                >
                                    <NavigationSectionHeading iconPath="icons/suitcase-outlined">
                                        {intl.formatMessage(messages.guestSectionLabel)}
                                    </NavigationSectionHeading>
                                    <Show when={environment.features.search}>
                                        <LinkAtomComponent {...controller.getMenuItemProps('/search')} href="/search">
                                            {intl.formatMessage(messages.searchLink)}
                                        </LinkAtomComponent>
                                        {/* TENANT-5565: the mobile app's way into Lodgr Insights; the website never routes /insights. */}
                                        <Show when={platform.isMobileApp}>
                                            <LinkAtomComponent class="public-template-component__main__sidebar__menu__item" href="/insights" onClick={controller.handleInsightsClick}>
                                                {intl.formatMessage(messages.lodgrInsightsButton)}
                                            </LinkAtomComponent>
                                        </Show>
                                    </Show>
                                    <LinkAtomComponent {...controller.getMenuItemProps('/stays')} onClick={controller.handleStaysClick}>
                                        {intl.formatMessage(messages.staysLink)}
                                    </LinkAtomComponent>
                                    <SeparatorAtomComponent class="public-template-component__main__sidebar__separator" />
                                    <NavigationSectionHeading iconPath="icons/house-outlined">
                                        {intl.formatMessage(messages.hostSectionLabel)}
                                    </NavigationSectionHeading>
                                    <LinkAtomComponent
                                        {...controller.getMenuItemProps('/properties')}
                                        classList={{
                                            'public-template-component__main__sidebar__menu__item--selected':
                                                selected(),
                                        }}
                                        onClick={controller.handlePropertiesClick}
                                    >
                                        {intl.formatMessage(messages.propertiesLink)}
                                    </LinkAtomComponent>
                                    <Show when={controller.countStore.get('numOfProperties') > 0}>
                                        <LinkAtomComponent
                                            {...controller.getMenuItemProps('/bookings')}
                                            onClick={controller.handleBookingsClick}
                                        >
                                            {intl.formatMessage(messages.bookingsLink)}
                                        </LinkAtomComponent>
                                        {/*
                                          * Host calendar (TENANT-5285). Gated on owning a listing for
                                          * the same reason Bookings is: with no listing there is no
                                          * calendar to show. `getMenuItemProps` compares the whole
                                          * pathname, so the item stops reading as selected once a
                                          * listing is chosen and the route becomes
                                          * `/calendar/{property_id}` — hence the explicit prefix
                                          * classList below rather than relying on that comparison.
                                          */}
                                        <LinkAtomComponent
                                            {...controller.getMenuItemProps('/calendar')}
                                            classList={{
                                                'public-template-component__main__sidebar__menu__item--selected':
                                                    controller.isCalendarRoute(),
                                            }}
                                            onClick={controller.handleCalendarClick}
                                        >
                                            {intl.formatMessage(messages.calendarLink)}
                                        </LinkAtomComponent>
                                    </Show>
                                    <SeparatorAtomComponent class="public-template-component__main__sidebar__separator" />
                                    <LinkAtomComponent
                                        {...controller.getMenuItemProps('/inbox')}
                                        onClick={controller.handleInboxClick}
                                    >
                                        {intl.formatMessage(messages.inboxLink)}
                                        <Show when={controller.unreadConvosCount() > 0}>
                                            {` (${controller.unreadConvosCount()})`}
                                        </Show>
                                    </LinkAtomComponent>
                                    <LinkAtomComponent
                                        {...controller.getMenuItemProps('/account')}
                                        onClick={controller.handleAccountClick}
                                    >
                                        {intl.formatMessage(messages.accountLink)}
                                    </LinkAtomComponent>
                                </MenuMoleculeComponent>
                                <MenuMoleculeComponent
                                    class="public-template-component__main__sidebar__menu--last"
                                    direction="vertical"
                                >
                                    <LinkAtomComponent
                                        onClick={controller.handleReferralsClick}
                                        {...controller.getMenuItemProps(ROUTES.REFERRAL_CENTER)}
                                    >
                                        {intl.formatMessage(messages.referralsLink)}
                                    </LinkAtomComponent>
                                    <LinkAtomComponent
                                        target="_blank"
                                        href="/faq/general"
                                        onClick={controller.handleFaqClick}
                                    >
                                        {intl.formatMessage(messages.faqLink)}
                                    </LinkAtomComponent>
                                    <ButtonAtomComponent
                                        onClick={controller.logout}
                                        class="public-template-component__main__sidebar__menu__button"
                                    >
                                        {intl.formatMessage(messages.logOutLink)}
                                    </ButtonAtomComponent>
                                </MenuMoleculeComponent>
                            </BarAtomComponent>
                        </Show>
                        <div
                            class="public-template-component__main__children"
                            classList={{
                                // Reserve viewport height on every route that renders the footer, so
                                // the footer starts below the fold while content loads and never
                                // shifts (TENANT-5134; extends the TENANT-5006 marketing-route fix).
                                'public-template-component__main__children--reserve-height': controller.state.showFooter,
                            }}
                        >
                            <Show when={controller.countStore.get('isLoaded') || isImmediateRenderRoute()}>
                                <Routes />
                            </Show>
                        </div>
                    </div>
                    <div class="public-template-component__footer-wrapper">
                        {/* Not in the mobile app: the footer is a web navigation surface, and the app does not include most of what it links to. */}
                        <Show when={!platform.isMobileApp && controller.state.showFooter}>
                            <FooterOrganismComponent class="public-template-component__footer" />
                        </Show>
                    </div>
                </Show>
                {/* Should gate both below to main authenticated dashboard */}


                <Show when={controller.state.showReferralSenderBanner}>
                    <BannerMoleculeComponent
                        class="public-template-component__banner-referral_sender"
                        hideIcon
                        hideCloseButton
                        body={
                            <>
                                <div class="public-template-component__banner-referral_sender__content">
                                    <div class="public-template-component__banner-referral_sender__content__title">
                                        {intl.formatMessage(messages.referralSenderBannerTitle)}
                                    </div>
                                    <div class="public-template-component__banner-referral_sender__content__body">
                                        {intl.formatMessage(messages.referralSenderBannerBody)}
                                    </div>
                                </div>
                                <div class="public-template-component__banner-referral_sender__footer">
                                    <ButtonAtomComponent class="public-template-component__banner-referral_sender__footer__button-right" onClick={controller.hideReferralSenderBanner}>{intl.formatMessage(messages.noThanksButton)}</ButtonAtomComponent>
                                    <ButtonAtomComponent class="public-template-component__banner-referral_sender__footer__button-left" onClick={controller.openReferralSenderModal}>{intl.formatMessage(messages.takeSurveyButton)}</ButtonAtomComponent>
                                </div>
                            </>
                        }
                    />
                </Show>
                <Show when={controller.state.showReferralRecipientBanner}>
                    <BannerMoleculeComponent
                        class="public-template-component__banner-referral_recipient"
                        hideIcon
                        hideCloseButton
                        body={
                            <>
                                <div class="public-template-component__banner__content">
                                    <div class="public-template-component__banner__content__title">
                                        {intl.formatMessage(messages.referralRecipientBannerTitle)}
                                    </div>

                                    <LikertMoleculeComponent
                                        options={controller.getReferralRecipientFeedbackOptions()}
                                        style="emoji"
                                        onChange={controller.handleReferralRecipientFeedback}
                                        showBorders={false}
                                    />

                                </div>

                            </>
                        }
                    />
                </Show>
                <Show when={controller.state.showReferralSenderModal}>
                    <ReferralSenderFeedbackModal
                        onClose={controller.closeReferralSenderModal}
                    />
                </Show>
                <Show when={controller.state.showReferralRecipientModal}>
                    <ReferralRecipientFeedbackModal
                        ratings={controller.referralRecipientFeedbackRating()}
                        onClose={controller.closeReferralRecipientModal}
                    />
                </Show>
                <Show when={controller.state.showLocaleSelectModal}>
                    <LocaleSelectModal
                        lockCurrencySelection={controller.state.lockCurrencySelection}
                        onClose={controller.closeLocaleSelectModal}
                    />
                </Show>
            </div>
        </PublicTemplateContext.Provider>
    )
}
